import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { AngleDown } from '@zooplus/icons';
import { getTestLabels } from '../../../utils/getTestLabels.js';
import { FormControlWrap } from '../FormControlWrap/FormControlWrap.js';
import textInputStyle from '../style/text-input.module.scss.js';
import selectStyle from './Select.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('Select');
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=3073-17787&node-type=section&t=L12XIxh9aK1lGzgC-0)
 *  | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Form/Select/Select.tsx)
 *
 * ### **Note:** see the [SelectField](https://ui-components.private.zooplus.net/?path=/docs/components-form-selectfield--docs) component for a more composed and fully accessible single `<select>` with associated label and hint and/or error messages.
 *
 * @param {boolean} hasError - (optional) Set to `true` when the select element is in an error state. Applies visual error styling.
 * @param {boolean} isDisabled - (optional) Set to `true` to visually and functionally disable the select element. This maps directly to the native HTML `disabled` attribute.
 * @param {boolean} isReadOnly - (optional) Set to `true` when the select is intended to be read-only. Applies visual styling.
 * @param {function} onChange - (optional) A custom function to call when the input value is changed.
 * @param {function} renderSelect - (optional) Use this to pass a completely custom `<select/>` element.
 * @param {object} selectProps - (optional) An object of native HTML Select element attributes ([see MDN docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select)) to pass to the select element.
 */
const Select = forwardRef((props, ref) => {
    const { children, hasError = false, isDisabled = false, isReadOnly = false, onChange, selectProps = {}, renderSelect, ...otherProps } = props;
    const handleKeyDown = (event) => {
        if (event.key === 'Tab')
            return;
        event.preventDefault();
    };
    const defaultRenderSelect = useCallback((props, ref) => {
        return (jsx("select", { "data-zta": elementTestLabel('Select'), ...props, ref: ref }));
    }, []);
    return (jsxs(FormControlWrap, { "data-zta": componentTestLabel, ...otherProps, children: [(renderSelect || defaultRenderSelect)({
                ...selectProps,
                className: classNames(textInputStyle['z-text-input'], selectStyle['z-select'], { [selectStyle['z-select--readonly']]: isReadOnly }, {
                    [textInputStyle['z-text-input--error']]: hasError,
                }, selectProps?.className),
                onKeyDown: isReadOnly ? handleKeyDown : undefined,
                onChange,
                disabled: isDisabled,
                children,
            }, ref), jsx(AngleDown, { "aria-hidden": true, className: classNames(selectStyle['z-select__arrow'], {
                    [selectStyle['z-select__arrow--error']]: hasError,
                }) })] }));
});
Select.displayName = 'Select';

export { Select };
//# sourceMappingURL=Select.js.map
